import { useCallback } from 'react';
import {
  cancelAnimation,
  useAnimatedReaction,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

const SCROLL_DOWN_THRESHOLD = 15;
const SCROLL_UP_THRESHOLD = 30;
const DIRECTION_CHANGE_MIN_DELTA = 3; // 방향 전환으로 인정하는 최소 이동량

export const useHomeScroll = () => {
  const headerVisible = useSharedValue(1);
  const scrollY = useSharedValue(0);

  // 방향 감지용 상태 (UI thread에서 유지)
  const isHidden = useSharedValue(0); // 0: visible, 1: hidden
  const lastScrollY = useSharedValue(0);
  const lastDirectionChangeY = useSharedValue(0);
  const lastDirection = useSharedValue(0); // 0: none, 1: up, -1: down

  useAnimatedReaction(
    () => scrollY.value,
    (currentY, prevY) => {
      if (prevY === null || prevY === undefined) return;

      // 최상단이면 항상 보이기
      if (currentY <= 0) {
        if (isHidden.value === 1) {
          cancelAnimation(headerVisible);
          headerVisible.value = withTiming(1, { duration: 200 });
          isHidden.value = 0;
        }
        lastScrollY.value = 0;
        lastDirectionChangeY.value = 0;
        lastDirection.value = 0;
        return;
      }

      const delta = currentY - lastScrollY.value;

      // 미세한 흔들림 무시
      if (Math.abs(delta) < DIRECTION_CHANGE_MIN_DELTA) {
        lastScrollY.value = currentY;
        return;
      }

      const currentDirection = delta > 0 ? -1 : 1; // -1: down, 1: up
      lastScrollY.value = currentY;

      // 방향이 바뀌면 기준점 리셋
      if (
        lastDirection.value !== 0 &&
        currentDirection !== lastDirection.value
      ) {
        lastDirectionChangeY.value = currentY;
      }
      lastDirection.value = currentDirection;

      const distance = Math.abs(currentY - lastDirectionChangeY.value);
      const threshold =
        currentDirection === -1 ? SCROLL_DOWN_THRESHOLD : SCROLL_UP_THRESHOLD;

      if (distance > threshold) {
        if (currentDirection === -1 && isHidden.value === 0) {
          cancelAnimation(headerVisible);
          headerVisible.value = withTiming(0, { duration: 200 });
          isHidden.value = 1;
        } else if (currentDirection === 1 && isHidden.value === 1) {
          cancelAnimation(headerVisible);
          headerVisible.value = withTiming(1, { duration: 200 });
          isHidden.value = 0;
        }
      }
    },
  );

  // 웹 등에서 JS thread에서 scrollY를 주입해야 하는 경우를 위한 호환 콜백
  const onScrollY = useCallback(
    (y: number) => {
      scrollY.value = y;
    },
    [scrollY],
  );

  return { headerVisible, scrollY, onScrollY };
};